/**
 * # TextField
 *
 * A text input field component.
 *
 * ## Props
 *
 * - `placeholder: string`: The placeholder text for the input field.
 * - `type: 'date' | 'email' | 'hidden' | 'password' | 'search' | 'tel' | 'text' | 'url'` Input types for the TextField.
 *
 * ## Usage
 *
 * ```jsx
 * import TextField from './TextField.react';
 *
 * function MyForm() {
 *   return (
 *     <form>
 *       <TextField placeholder="Enter your email" type="email" />
 *     </form>
 *   );
 * }
 * ```
 */
import React, {forwardRef} from 'react';
import PropTypes from 'prop-types';
import Input from '@albert-io/atomic/atoms/Input/Input.react';

import type {FieldProps} from '../Field/Field.react';
import Field from '../Field/Field.react';

type TextFieldProps = FieldProps<typeof Input>;

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({type = 'text', ...props}, ref) => (
  <Field ref={ref} as={Input} type={type} {...props} />
));

TextField.propTypes = {
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(['date', 'email', 'hidden', 'password', 'search', 'tel', 'text', 'url'])
};

TextField.displayName = 'TextField';

export default TextField;
